/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/configs/customerRegistrationConfigs.ts
 * Release: 1.4.0
 * Descrizione: Configurazioni JSON per il form di registrazione cliente con paesi EU e icone
 */

import type { FormConfig } from '../types';
import { 
  BuildingIcon, 
  UserGroupIcon, 
  UserIcon, 
  MapPinIcon,
  DocumentIcon,
  PhoneIcon,
  EmailIcon,
  UserCircleIcon,
  KeyIcon,
  GlobeIcon,
  CreditCardIcon,
  TruckIcon
} from '../icons/index';

export const aziendaConfig: FormConfig = {
  id: 'customer-registration-azienda',
  title: 'Registrazione Cliente - Azienda',
  description: 'Compila i dati della tua azienda per completare la registrazione',
  groups: [
    {
      id: 'dati-azienda',
      title: 'Dati Azienda',
      description: 'Informazioni principali dell\'azienda',
      icon: BuildingIcon,
      collapsible: true,
      collapsed: false,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      validation: {
        custom: (values: Record<string, any>) => {
          // Controlla se il paese è Italia
          if (values.prefissoNazione === 'IT') {
            const codiceUnivoco = values.codiceUnivoco || '';
            const pec = values.pec || '';
            
            // Se entrambi i campi sono vuoti, mostra errore
            if (codiceUnivoco.trim() === '' && pec.trim() === '') {
              return 'Per l\'Italia è obbligatorio compilare almeno uno dei due campi: Codice Univoco o PEC';
            }
          }
          return undefined;
        }
      },
      rows: [
        // Prima riga aggiornata
        {
          id: 'row-1',
          columns: [
            {
              id: 'ragioneSociale',
              field: {
                type: 'text',
                label: 'Ragione Sociale',
                placeholder: 'Inserisci la ragione sociale',
                required: false,
                formatter: 'capitalizeWords',
                icon: BuildingIcon
              },
              width: '1/2' // 50%
            },
            {
              id: 'prefissoNazione',
              field: {
                type: 'custom',
                customComponent: 'CountrySelectField',
                label: 'Prefisso Nazione',
                placeholder: 'Seleziona nazione',
                required: false,
                defaultValue: 'IT',
                icon: GlobeIcon,
                options: [
                  { value: 'IT', label: 'Italia', flag: 'it' },
                  { value: 'DE', label: 'Germania', flag: 'de' },
                  { value: 'FR', label: 'Francia', flag: 'fr' },
                  { value: 'ES', label: 'Spagna', flag: 'es' },
                  { value: 'GB', label: 'Regno Unito', flag: 'gb' },
                  { value: 'AT', label: 'Austria', flag: 'at' },
                  { value: 'BE', label: 'Belgio', flag: 'be' },
                  { value: 'CH', label: 'Svizzera', flag: 'ch' },
                  { value: 'NL', label: 'Paesi Bassi', flag: 'nl' },
                  { value: 'SE', label: 'Svezia', flag: 'se' },
                  { value: 'NO', label: 'Norvegia', flag: 'no' },
                  { value: 'DK', label: 'Danimarca', flag: 'dk' },
                  { value: 'FI', label: 'Finlandia', flag: 'fi' },
                  { value: 'PL', label: 'Polonia', flag: 'pl' },
                  { value: 'CZ', label: 'Repubblica Ceca', flag: 'cz' },
                  { value: 'HU', label: 'Ungheria', flag: 'hu' },
                  { value: 'RO', label: 'Romania', flag: 'ro' },
                  { value: 'BG', label: 'Bulgaria', flag: 'bg' },
                  { value: 'HR', label: 'Croazia', flag: 'hr' },
                  { value: 'SI', label: 'Slovenia', flag: 'si' },
                  { value: 'SK', label: 'Slovacchia', flag: 'sk' },
                  { value: 'LT', label: 'Lituania', flag: 'lt' },
                  { value: 'LV', label: 'Lettonia', flag: 'lv' },
                  { value: 'EE', label: 'Estonia', flag: 'ee' },
                  { value: 'IE', label: 'Irlanda', flag: 'ie' },
                  { value: 'PT', label: 'Portogallo', flag: 'pt' },
                  { value: 'GR', label: 'Grecia', flag: 'gr' },
                  { value: 'CY', label: 'Cipro', flag: 'cy' },
                  { value: 'MT', label: 'Malta', flag: 'mt' },
                  { value: 'LU', label: 'Lussemburgo', flag: 'lu' },
                  // Paesi extra-UE
                  { value: 'US', label: 'Stati Uniti', flag: 'us' },
                  { value: 'CA', label: 'Canada', flag: 'ca' },
                  { value: 'AU', label: 'Australia', flag: 'au' },
                  { value: 'JP', label: 'Giappone', flag: 'jp' },
                  { value: 'CN', label: 'Cina', flag: 'cn' },
                  { value: 'IN', label: 'India', flag: 'in' },
                  { value: 'BR', label: 'Brasile', flag: 'br' },
                  { value: 'MX', label: 'Messico', flag: 'mx' }
                ]
              },
              width: '1/4' // 25%
            },
            {
              id: 'partitaIva',
              field: {
                type: 'text',
                label: 'Partita IVA',
                placeholder: 'Inserisci la P.IVA',
                required: false,
                icon: DocumentIcon
              },
              width: '1/4' // 25%
            }
          ]
        },
        // Seconda riga
        {
          id: 'row-2',
          columns: [
            {
              id: 'codiceUnivoco',
              field: {
                type: 'text',
                label: 'Codice Univoco',
                placeholder: 'Inserisci il codice univoco',
                required: false,
                icon: KeyIcon,
                formatter: 'toUpperCase',
                validation: {
                  pattern: '^[A-Z0-9]+$',
                  message: 'Sono ammessi solo caratteri alfanumerici maiuscoli'
                }
              },
              width: '1/2'
            },
            {
              id: 'pec',
              field: {
                type: 'email',
                label: 'PEC',
                placeholder: 'Inserisci la PEC',
                required: false,
                icon: EmailIcon,
                validation: {
                  email: true
                }
              },
              width: '1/2'
            }
          ]
        },
        // Terza riga aggiornata
        {
          id: 'row-3',
          columns: [
            {
              id: 'email',
              field: {
                type: 'email',
                label: 'Email',
                placeholder: 'Inserisci l\'email',
                required: false,
                icon: EmailIcon
              },
              width: '1/3'
            },
            {
              id: 'prefissoTelefono',
              field: {
                type: 'custom',
                customComponent: 'PhonePrefixSelectField',
                label: 'Prefisso Tel.',
                placeholder: 'Seleziona prefisso',
                required: false,
                // defaultValue: '+39', // Rimosso per permettere il caricamento dei dati dal DB
                icon: PhoneIcon,
                options: [
                  // Europa
                  { value: '+39', label: 'Italia', flag: 'it', group: 'Europa' },
                  { value: '+33', label: 'Francia', flag: 'fr', group: 'Europa' },
                  { value: '+49', label: 'Germania', flag: 'de', group: 'Europa' },
                  { value: '+44', label: 'Regno Unito', flag: 'gb', group: 'Europa' },
                  { value: '+34', label: 'Spagna', flag: 'es', group: 'Europa' },
                  { value: '+43', label: 'Austria', flag: 'at', group: 'Europa' },
                  { value: '+32', label: 'Belgio', flag: 'be', group: 'Europa' },
                  { value: '+41', label: 'Svizzera', flag: 'ch', group: 'Europa' },
                  { value: '+31', label: 'Paesi Bassi', flag: 'nl', group: 'Europa' },
                  { value: '+46', label: 'Svezia', flag: 'se', group: 'Europa' },
                  { value: '+47', label: 'Norvegia', flag: 'no', group: 'Europa' },
                  { value: '+45', label: 'Danimarca', flag: 'dk', group: 'Europa' },
                  { value: '+358', label: 'Finlandia', flag: 'fi', group: 'Europa' },
                  { value: '+48', label: 'Polonia', flag: 'pl', group: 'Europa' },
                  { value: '+420', label: 'Repubblica Ceca', flag: 'cz', group: 'Europa' },
                  { value: '+36', label: 'Ungheria', flag: 'hu', group: 'Europa' },
                  { value: '+40', label: 'Romania', flag: 'ro', group: 'Europa' },
                  { value: '+359', label: 'Bulgaria', flag: 'bg', group: 'Europa' },
                  { value: '+385', label: 'Croazia', flag: 'hr', group: 'Europa' },
                  { value: '+386', label: 'Slovenia', flag: 'si', group: 'Europa' },
                  { value: '+421', label: 'Slovacchia', flag: 'sk', group: 'Europa' },
                  { value: '+370', label: 'Lituania', flag: 'lt', group: 'Europa' },
                  { value: '+371', label: 'Lettonia', flag: 'lv', group: 'Europa' },
                  { value: '+372', label: 'Estonia', flag: 'ee', group: 'Europa' },
                  { value: '+353', label: 'Irlanda', flag: 'ie', group: 'Europa' },
                  { value: '+351', label: 'Portogallo', flag: 'pt', group: 'Europa' },
                  { value: '+30', label: 'Grecia', flag: 'gr', group: 'Europa' },
                  { value: '+357', label: 'Cipro', flag: 'cy', group: 'Europa' },
                  { value: '+356', label: 'Malta', flag: 'mt', group: 'Europa' },
                  { value: '+352', label: 'Lussemburgo', flag: 'lu', group: 'Europa' },
                  
                  // Americhe
                  { value: '+1', label: 'Stati Uniti', flag: 'us', group: 'Americhe' },
                  { value: '+1', label: 'Canada', flag: 'ca', group: 'Americhe' },
                  { value: '+52', label: 'Messico', flag: 'mx', group: 'Americhe' },
                  { value: '+55', label: 'Brasile', flag: 'br', group: 'Americhe' },
                  { value: '+54', label: 'Argentina', flag: 'ar', group: 'Americhe' },
                  { value: '+56', label: 'Cile', flag: 'cl', group: 'Americhe' },
                  { value: '+57', label: 'Colombia', flag: 'co', group: 'Americhe' },
                  { value: '+51', label: 'Perù', flag: 'pe', group: 'Americhe' },
                  { value: '+58', label: 'Venezuela', flag: 've', group: 'Americhe' },
                  { value: '+598', label: 'Uruguay', flag: 'uy', group: 'Americhe' },
                  { value: '+595', label: 'Paraguay', flag: 'py', group: 'Americhe' },
                  { value: '+593', label: 'Ecuador', flag: 'ec', group: 'Americhe' },
                  { value: '+591', label: 'Bolivia', flag: 'bo', group: 'Americhe' },
                  
                  // Asia
                  { value: '+86', label: 'Cina', flag: 'cn', group: 'Asia' },
                  { value: '+81', label: 'Giappone', flag: 'jp', group: 'Asia' },
                  { value: '+82', label: 'Corea del Sud', flag: 'kr', group: 'Asia' },
                  { value: '+91', label: 'India', flag: 'in', group: 'Asia' },
                  { value: '+65', label: 'Singapore', flag: 'sg', group: 'Asia' },
                  { value: '+60', label: 'Malesia', flag: 'my', group: 'Asia' },
                  { value: '+66', label: 'Thailandia', flag: 'th', group: 'Asia' },
                  { value: '+84', label: 'Vietnam', flag: 'vn', group: 'Asia' },
                  { value: '+63', label: 'Filippine', flag: 'ph', group: 'Asia' },
                  { value: '+62', label: 'Indonesia', flag: 'id', group: 'Asia' },
                  { value: '+886', label: 'Taiwan', flag: 'tw', group: 'Asia' },
                  { value: '+852', label: 'Hong Kong', flag: 'hk', group: 'Asia' },
                  { value: '+971', label: 'Emirati Arabi Uniti', flag: 'ae', group: 'Asia' },
                  { value: '+966', label: 'Arabia Saudita', flag: 'sa', group: 'Asia' },
                  { value: '+972', label: 'Israele', flag: 'il', group: 'Asia' },
                  { value: '+90', label: 'Turchia', flag: 'tr', group: 'Asia' },
                  
                  // Africa
                  { value: '+27', label: 'Sudafrica', flag: 'za', group: 'Africa' },
                  { value: '+20', label: 'Egitto', flag: 'eg', group: 'Africa' },
                  { value: '+234', label: 'Nigeria', flag: 'ng', group: 'Africa' },
                  { value: '+254', label: 'Kenya', flag: 'ke', group: 'Africa' },
                  { value: '+212', label: 'Marocco', flag: 'ma', group: 'Africa' },
                  { value: '+216', label: 'Tunisia', flag: 'tn', group: 'Africa' },
                  { value: '+213', label: 'Algeria', flag: 'dz', group: 'Africa' },
                  { value: '+233', label: 'Ghana', flag: 'gh', group: 'Africa' },
                  { value: '+251', label: 'Etiopia', flag: 'et', group: 'Africa' },
                  
                  // Oceania
                  { value: '+61', label: 'Australia', flag: 'au', group: 'Oceania' },
                  { value: '+64', label: 'Nuova Zelanda', flag: 'nz', group: 'Oceania' },
                  { value: '+679', label: 'Fiji', flag: 'fj', group: 'Oceania' },
                  { value: '+675', label: 'Papua Nuova Guinea', flag: 'pg', group: 'Oceania' }
                ]
              },
              width: '1/3'
            },
            {
              id: 'telefono',
              field: {
                type: 'text',
                label: 'Telefono',
                placeholder: 'Inserisci il numero di telefono',
                required: true,
                validation: {
                  pattern: '^[0-9.\-]+$',
                  message: 'Sono ammessi solo numeri, punti e trattini (-)'
                }
              },
              width: '1/3'
            }
          ]
        },
        // Riga sotto telefono: SocialCheckboxRow custom
        {
          id: 'row-4',
          columns: [
            {
              id: 'telefonoSocial',
              field: {
                type: 'custom',
                customComponent: 'SocialCheckboxRow',
                label: '',
                options: [{ value: 'whatsapp', label: 'WhatsApp' }, { value: 'telegram', label: 'Telegram' }]
              },
              width: 'full'
            }
          ]
        },
      ]
    },
    {
      id: 'contatti-referente',
      title: 'Contatti Referente Principale',
      description: 'Informazioni dei referenti aziendali',
      icon: UserGroupIcon,
      collapsible: true,
      collapsed: false,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'user', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        // Referente Principale - Prima riga: Titolo, Nome*, Cognome*, Email
        {
          id: 'referente-principale-row-1',
          columns: [
            {
              id: 'referentePrincipaleTitolo',
              field: {
                type: 'select',
                label: 'Titolo',
                placeholder: 'Seleziona titolo',
                required: false,
                defaultValue: 'sig',
                icon: UserCircleIcon,
                options: [
                  { value: 'sig', label: 'Sig' },
                  { value: 'sig.ra', label: 'Sig.ra' },
                  { value: 'dott', label: 'Dott' },
                  { value: 'dott.ssa', label: 'Dott.ssa' },
                  { value: 'ing', label: 'Ing' },
                  { value: 'avv', label: 'Avv' },
                  { value: 'prof', label: 'Prof' },
                  { value: 'prof.ssa', label: 'Prof.ssa' }
                ]
              },
              width: '2/12'
            },
            {
              id: 'referentePrincipaleNome',
              field: {
                type: 'text',
                label: 'Nome',
                placeholder: 'Inserisci il nome',
                required: false,
                formatter: 'capitalizeWords',
                icon: UserCircleIcon
              },
              width: '3/12'
            },
            {
              id: 'referentePrincipaleCognome',
              field: {
                type: 'text',
                label: 'Cognome',
                placeholder: 'Inserisci il cognome',
                required: false,
                formatter: 'capitalizeWords',
                icon: UserCircleIcon
              },
              width: '3/12'
            },
            {
              id: 'referentePrincipaleEmail',
              field: {
                type: 'email',
                label: 'E-Mail',
                placeholder: 'Inserisci l\'email',
                required: false,
                icon: EmailIcon,
                validation: {
                  email: true
                }
              },
              width: '4/12'
            }
          ]
        },
        // Referente Principale - Seconda riga: Prefisso, Telefono, Prefisso Cell, Cellulare
        {
          id: 'referente-principale-row-2',
          columns: [
            {
              id: 'referentePrincipalePrefissoFisso',
              field: {
                type: 'custom',
                customComponent: 'PhonePrefixSelectField',
                label: 'Prefisso Int.',
                placeholder: 'Seleziona prefisso',
                required: false,
                // defaultValue: '+39', // Rimosso per permettere il caricamento dei dati dal DB
                icon: PhoneIcon,
                options: [
                  // Europa
                  { value: '+39', label: 'Italia', flag: 'it', group: 'Europa' },
                  { value: '+33', label: 'Francia', flag: 'fr', group: 'Europa' },
                  { value: '+49', label: 'Germania', flag: 'de', group: 'Europa' },
                  { value: '+44', label: 'Regno Unito', flag: 'gb', group: 'Europa' },
                  { value: '+34', label: 'Spagna', flag: 'es', group: 'Europa' },
                  { value: '+43', label: 'Austria', flag: 'at', group: 'Europa' },
                  { value: '+32', label: 'Belgio', flag: 'be', group: 'Europa' },
                  { value: '+41', label: 'Svizzera', flag: 'ch', group: 'Europa' },
                  { value: '+31', label: 'Paesi Bassi', flag: 'nl', group: 'Europa' },
                  { value: '+46', label: 'Svezia', flag: 'se', group: 'Europa' },
                  { value: '+47', label: 'Norvegia', flag: 'no', group: 'Europa' },
                  { value: '+45', label: 'Danimarca', flag: 'dk', group: 'Europa' },
                  { value: '+358', label: 'Finlandia', flag: 'fi', group: 'Europa' },
                  { value: '+48', label: 'Polonia', flag: 'pl', group: 'Europa' },
                  { value: '+420', label: 'Repubblica Ceca', flag: 'cz', group: 'Europa' },
                  { value: '+36', label: 'Ungheria', flag: 'hu', group: 'Europa' },
                  { value: '+40', label: 'Romania', flag: 'ro', group: 'Europa' },
                  { value: '+359', label: 'Bulgaria', flag: 'bg', group: 'Europa' },
                  { value: '+385', label: 'Croazia', flag: 'hr', group: 'Europa' },
                  { value: '+386', label: 'Slovenia', flag: 'si', group: 'Europa' },
                  { value: '+421', label: 'Slovacchia', flag: 'sk', group: 'Europa' },
                  { value: '+370', label: 'Lituania', flag: 'lt', group: 'Europa' },
                  { value: '+371', label: 'Lettonia', flag: 'lv', group: 'Europa' },
                  { value: '+372', label: 'Estonia', flag: 'ee', group: 'Europa' },
                  { value: '+353', label: 'Irlanda', flag: 'ie', group: 'Europa' },
                  { value: '+351', label: 'Portogallo', flag: 'pt', group: 'Europa' },
                  { value: '+30', label: 'Grecia', flag: 'gr', group: 'Europa' },
                  { value: '+357', label: 'Cipro', flag: 'cy', group: 'Europa' },
                  { value: '+356', label: 'Malta', flag: 'mt', group: 'Europa' },
                  { value: '+352', label: 'Lussemburgo', flag: 'lu', group: 'Europa' }
                ]
              },
              width: '3/12'
            },
            {
              id: 'referentePrincipaleTelefono',
              field: {
                type: 'phone',
                label: 'Telefono Fisso',
                placeholder: 'Inserisci numero di telefono fisso',
                required: false,
                icon: PhoneIcon,
                validation: {
                  pattern: '^[0-9.\-]+$',
                  message: 'Sono ammessi solo numeri, punti e trattini (-)'
                }
              },
              width: '3/12'
            },
            {
              id: 'referentePrincipalePrefissoCellulare',
              field: {
                type: 'custom',
                customComponent: 'PhonePrefixSelectField',
                label: 'Prefisso Int.',
                placeholder: 'Seleziona prefisso',
                required: false,
                // defaultValue: '+39', // Rimosso per permettere il caricamento dei dati dal DB
                icon: PhoneIcon,
                options: [
                  // Europa
                  { value: '+39', label: 'Italia', flag: 'it', group: 'Europa' },
                  { value: '+33', label: 'Francia', flag: 'fr', group: 'Europa' },
                  { value: '+49', label: 'Germania', flag: 'de', group: 'Europa' },
                  { value: '+44', label: 'Regno Unito', flag: 'gb', group: 'Europa' },
                  { value: '+34', label: 'Spagna', flag: 'es', group: 'Europa' },
                  { value: '+43', label: 'Austria', flag: 'at', group: 'Europa' },
                  { value: '+32', label: 'Belgio', flag: 'be', group: 'Europa' },
                  { value: '+41', label: 'Svizzera', flag: 'ch', group: 'Europa' },
                  { value: '+31', label: 'Paesi Bassi', flag: 'nl', group: 'Europa' },
                  { value: '+46', label: 'Svezia', flag: 'se', group: 'Europa' },
                  { value: '+47', label: 'Norvegia', flag: 'no', group: 'Europa' },
                  { value: '+45', label: 'Danimarca', flag: 'dk', group: 'Europa' },
                  { value: '+358', label: 'Finlandia', flag: 'fi', group: 'Europa' },
                  { value: '+48', label: 'Polonia', flag: 'pl', group: 'Europa' },
                  { value: '+420', label: 'Repubblica Ceca', flag: 'cz', group: 'Europa' },
                  { value: '+36', label: 'Ungheria', flag: 'hu', group: 'Europa' },
                  { value: '+40', label: 'Romania', flag: 'ro', group: 'Europa' },
                  { value: '+359', label: 'Bulgaria', flag: 'bg', group: 'Europa' },
                  { value: '+385', label: 'Croazia', flag: 'hr', group: 'Europa' },
                  { value: '+386', label: 'Slovenia', flag: 'si', group: 'Europa' },
                  { value: '+421', label: 'Slovacchia', flag: 'sk', group: 'Europa' },
                  { value: '+370', label: 'Lituania', flag: 'lt', group: 'Europa' },
                  { value: '+371', label: 'Lettonia', flag: 'lv', group: 'Europa' },
                  { value: '+372', label: 'Estonia', flag: 'ee', group: 'Europa' },
                  { value: '+353', label: 'Irlanda', flag: 'ie', group: 'Europa' },
                  { value: '+351', label: 'Portogallo', flag: 'pt', group: 'Europa' },
                  { value: '+30', label: 'Grecia', flag: 'gr', group: 'Europa' },
                  { value: '+357', label: 'Cipro', flag: 'cy', group: 'Europa' },
                  { value: '+356', label: 'Malta', flag: 'mt', group: 'Europa' },
                  { value: '+352', label: 'Lussemburgo', flag: 'lu', group: 'Europa' }
                ]
              },
              width: '3/12'
            },
            {
              id: 'referentePrincipaleCellulare',
              field: {
                type: 'phone',
                label: 'Cellulare',
                placeholder: 'Inserisci numero di cellulare',
                required: false,
                icon: PhoneIcon,
                validation: {
                  pattern: '^[0-9.\-]+$',
                  message: 'Sono ammessi solo numeri, punti e trattini (-)'
                }
              },
              width: '3/12'
            }
          ]
        },
        // Referente Principale - Terza riga: SocialCheckboxRow custom
        {
          id: 'referente-principale-row-3',
          columns: [
            {
              id: 'referentePrincipaleSocial',
              field: {
                type: 'custom',
                customComponent: 'SocialCheckboxRow',
                label: '',
                required: false,
                defaultValue: { whatsapp: false, telegram: false }
              },
              width: 'full'
            }
          ]
        }
      ]
    },
    {
      id: 'referente-secondario',
      title: 'Referente Secondario',
      description: 'Informazioni del referente secondario (opzionale)',
      icon: UserIcon,
      collapsible: true,
      collapsed: true, // RIPRISTINATO: Sezione chiusa di default
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'user', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        // Referente Secondario - Prima riga: Titolo, Nome, Cognome, Email
        {
          id: 'referente-secondario-row-1',
          columns: [
            {
              id: 'referenteSecondarioTitolo',
              field: {
                type: 'select',
                label: 'Titolo',
                placeholder: 'Seleziona titolo',
                required: false,
                defaultValue: 'sig',
                options: [
                  { value: 'sig', label: 'Sig' },
                  { value: 'sig.ra', label: 'Sig.ra' },
                  { value: 'dott', label: 'Dott' },
                  { value: 'dott.ssa', label: 'Dott.ssa' },
                  { value: 'ing', label: 'Ing' },
                  { value: 'avv', label: 'Avv' },
                  { value: 'prof', label: 'Prof' },
                  { value: 'prof.ssa', label: 'Prof.ssa' }
                ]
              },
              width: '2/12'
            },
            {
              id: 'referenteSecondarioNome',
              field: {
                type: 'text',
                label: 'Nome',
                placeholder: 'Inserisci il nome',
                required: false,
                formatter: 'capitalizeWords'
              },
              width: '3/12'
            },
            {
              id: 'referenteSecondarioCognome',
              field: {
                type: 'text',
                label: 'Cognome',
                placeholder: 'Inserisci il cognome',
                required: false,
                formatter: 'capitalizeWords'
              },
              width: '3/12'
            },
            {
              id: 'referenteSecondarioEmail',
              field: {
                type: 'email',
                label: 'E-Mail',
                placeholder: 'Inserisci l\'email',
                required: false,
                validation: {
                  email: true
                }
              },
              width: '4/12'
            }
          ]
        },
        // Referente Secondario - Seconda riga: Prefisso, Telefono, Prefisso Cell, Cellulare
        {
          id: 'referente-secondario-row-2',
          columns: [
            {
              id: 'referenteSecondarioPrefissoFisso',
              field: {
                type: 'custom',
                customComponent: 'PhonePrefixSelectField',
                label: 'Prefisso Int.',
                placeholder: 'Seleziona prefisso',
                required: false,
                // defaultValue: '+39', // Rimosso per permettere il caricamento dei dati dal DB
                icon: PhoneIcon,
                options: [
                  // Europa
                  { value: '+39', label: 'Italia', flag: 'it', group: 'Europa' },
                  { value: '+33', label: 'Francia', flag: 'fr', group: 'Europa' },
                  { value: '+49', label: 'Germania', flag: 'de', group: 'Europa' },
                  { value: '+44', label: 'Regno Unito', flag: 'gb', group: 'Europa' },
                  { value: '+34', label: 'Spagna', flag: 'es', group: 'Europa' },
                  { value: '+43', label: 'Austria', flag: 'at', group: 'Europa' },
                  { value: '+32', label: 'Belgio', flag: 'be', group: 'Europa' },
                  { value: '+41', label: 'Svizzera', flag: 'ch', group: 'Europa' },
                  { value: '+31', label: 'Paesi Bassi', flag: 'nl', group: 'Europa' },
                  { value: '+46', label: 'Svezia', flag: 'se', group: 'Europa' },
                  { value: '+47', label: 'Norvegia', flag: 'no', group: 'Europa' },
                  { value: '+45', label: 'Danimarca', flag: 'dk', group: 'Europa' },
                  { value: '+358', label: 'Finlandia', flag: 'fi', group: 'Europa' },
                  { value: '+48', label: 'Polonia', flag: 'pl', group: 'Europa' },
                  { value: '+420', label: 'Repubblica Ceca', flag: 'cz', group: 'Europa' },
                  { value: '+36', label: 'Ungheria', flag: 'hu', group: 'Europa' },
                  { value: '+40', label: 'Romania', flag: 'ro', group: 'Europa' },
                  { value: '+359', label: 'Bulgaria', flag: 'bg', group: 'Europa' },
                  { value: '+385', label: 'Croazia', flag: 'hr', group: 'Europa' },
                  { value: '+386', label: 'Slovenia', flag: 'si', group: 'Europa' },
                  { value: '+421', label: 'Slovacchia', flag: 'sk', group: 'Europa' },
                  { value: '+370', label: 'Lituania', flag: 'lt', group: 'Europa' },
                  { value: '+371', label: 'Lettonia', flag: 'lv', group: 'Europa' },
                  { value: '+372', label: 'Estonia', flag: 'ee', group: 'Europa' },
                  { value: '+353', label: 'Irlanda', flag: 'ie', group: 'Europa' },
                  { value: '+351', label: 'Portogallo', flag: 'pt', group: 'Europa' },
                  { value: '+30', label: 'Grecia', flag: 'gr', group: 'Europa' },
                  { value: '+357', label: 'Cipro', flag: 'cy', group: 'Europa' },
                  { value: '+356', label: 'Malta', flag: 'mt', group: 'Europa' },
                  { value: '+352', label: 'Lussemburgo', flag: 'lu', group: 'Europa' }
                ]
              },
              width: '3/12'
            },
            {
              id: 'referenteSecondarioTelefono',
              field: {
                type: 'phone',
                label: 'Telefono Fisso',
                placeholder: 'Inserisci numero di telefono fisso',
                required: false,
                validation: {
                  pattern: '^[0-9.\-]+$',
                  message: 'Sono ammessi solo numeri, punti e trattini (-)'
                }
              },
              width: '3/12'
            },
            {
              id: 'referenteSecondarioPrefissoCellulare',
              field: {
                type: 'custom',
                customComponent: 'PhonePrefixSelectField',
                label: 'Prefisso Int.',
                placeholder: 'Seleziona prefisso',
                required: false,
                // defaultValue: '+39', // Rimosso per permettere il caricamento dei dati dal DB
                options: [
                  // Europa
                  { value: '+39', label: 'Italia', flag: 'it', group: 'Europa' },
                  { value: '+33', label: 'Francia', flag: 'fr', group: 'Europa' },
                  { value: '+49', label: 'Germania', flag: 'de', group: 'Europa' },
                  { value: '+44', label: 'Regno Unito', flag: 'gb', group: 'Europa' },
                  { value: '+34', label: 'Spagna', flag: 'es', group: 'Europa' },
                  { value: '+43', label: 'Austria', flag: 'at', group: 'Europa' },
                  { value: '+32', label: 'Belgio', flag: 'be', group: 'Europa' },
                  { value: '+41', label: 'Svizzera', flag: 'ch', group: 'Europa' },
                  { value: '+31', label: 'Paesi Bassi', flag: 'nl', group: 'Europa' },
                  { value: '+46', label: 'Svezia', flag: 'se', group: 'Europa' },
                  { value: '+47', label: 'Norvegia', flag: 'no', group: 'Europa' },
                  { value: '+45', label: 'Danimarca', flag: 'dk', group: 'Europa' },
                  { value: '+358', label: 'Finlandia', flag: 'fi', group: 'Europa' },
                  { value: '+48', label: 'Polonia', flag: 'pl', group: 'Europa' },
                  { value: '+420', label: 'Repubblica Ceca', flag: 'cz', group: 'Europa' },
                  { value: '+36', label: 'Ungheria', flag: 'hu', group: 'Europa' },
                  { value: '+40', label: 'Romania', flag: 'ro', group: 'Europa' },
                  { value: '+359', label: 'Bulgaria', flag: 'bg', group: 'Europa' },
                  { value: '+385', label: 'Croazia', flag: 'hr', group: 'Europa' },
                  { value: '+386', label: 'Slovenia', flag: 'si', group: 'Europa' },
                  { value: '+421', label: 'Slovacchia', flag: 'sk', group: 'Europa' },
                  { value: '+370', label: 'Lituania', flag: 'lt', group: 'Europa' },
                  { value: '+371', label: 'Lettonia', flag: 'lv', group: 'Europa' },
                  { value: '+372', label: 'Estonia', flag: 'ee', group: 'Europa' },
                  { value: '+353', label: 'Irlanda', flag: 'ie', group: 'Europa' },
                  { value: '+351', label: 'Portogallo', flag: 'pt', group: 'Europa' },
                  { value: '+30', label: 'Grecia', flag: 'gr', group: 'Europa' },
                  { value: '+357', label: 'Cipro', flag: 'cy', group: 'Europa' },
                  { value: '+356', label: 'Malta', flag: 'mt', group: 'Europa' },
                  { value: '+352', label: 'Lussemburgo', flag: 'lu', group: 'Europa' }
                ]
              },
              width: '3/12'
            },
            {
              id: 'referenteSecondarioCellulare',
              field: {
                type: 'phone',
                label: 'Cellulare',
                placeholder: 'Inserisci numero di cellulare',
                required: false,
                validation: {
                  pattern: '^[0-9.\-]+$',
                  message: 'Sono ammessi solo numeri, punti e trattini (-)'
                }
              },
              width: '3/12'
            }
          ]
        },
        // Referente Secondario - Terza riga: SocialCheckboxRow custom
        {
          id: 'referente-secondario-row-3',
          columns: [
            {
              id: 'referenteSecondarioSocial',
              field: {
                type: 'custom',
                customComponent: 'SocialCheckboxRow',
                label: '',
                required: false,
                defaultValue: { whatsapp: false, telegram: false }
              },
              width: 'full'
            }
          ]
        }
      ]
    },
    {
      id: 'indirizzo',
      title: 'Indirizzo',
      description: 'Sede legale e operativa',
      icon: MapPinIcon,
      collapsible: true,
      collapsed: false,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        {
          id: 'row-1',
          columns: [
            {
              id: 'indirizzo',
              field: {
                type: 'custom',
                customComponent: 'AddressSection',
                label: 'Cerca Indirizzo',
                placeholder: 'Cerca indirizzo...',
                required: false,
                helpText: 'Cerca un indirizzo oppure compila manualmente i campi sottostanti'
              },
              width: 'full'
            }
          ]
        },
        {
          id: 'row-2',
          columns: [
            {
              id: 'noteIndirizzo',
              field: {
                type: 'text',
                label: 'Note Indirizzo',
                placeholder: 'Note aggiuntive per l\'indirizzo (max 500 caratteri)',
                required: false,
                maxLength: 500,
                helpText: 'Puoi utilizzare la formattazione per evidenziare testo, creare liste o aggiungere collegamenti'
              },
              width: 'full'
            }
          ]
        }
      ]
    },
    {
      id: 'note',
      title: 'Note Aggiuntive',
      description: 'Informazioni aggiuntive',
      collapsible: true,
      collapsed: true,
      permissions: {
        view: ['admin', 'manager', 'superadmin'],
        edit: ['admin', 'manager', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        {
          id: 'row-1',
          columns: [
            {
              id: 'note',
              field: {
                type: 'text',
                label: 'Note aggiuntive',
                placeholder: 'Inserisci eventuali note, richieste particolari o informazioni aggiuntive (max 500 caratteri)...',
                required: false,
                maxLength: 500,
                helpText: 'Puoi utilizzare la formattazione per evidenziare testo, creare liste o aggiungere collegamenti'
              },
              width: 'full'
            }
          ]
        }
      ]
    },
    {
      id: 'modalita-pagamento',
      title: 'Modalità di Pagamento',
      description: 'Scegli le tue modalità di pagamento preferite',
      icon: CreditCardIcon,
      collapsible: true,
      collapsed: true,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'user', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        {
          id: 'payment-methods-row',
          columns: [
            {
              id: 'paymentMethods',
              field: {
                type: 'custom',
                customComponent: 'PaymentMethodsCustom',
                label: '',
                required: false,
                defaultValue: { methods: [], preferences: {} }
              },
              width: 'full'
            }
          ]
        }
      ]
    },
    {
      id: 'modalita-consegna',
      title: 'Modalità di Consegna',
      description: 'Scegli le tue modalità di consegna preferite',
      icon: TruckIcon,
      collapsible: true,
      collapsed: true,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'user', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        {
          id: 'delivery-methods-row',
          columns: [
            {
              id: 'deliveryMethods',
              field: {
                type: 'custom',
                customComponent: 'DeliveryMethodsCustom',
                label: '',
                required: false,
                defaultValue: { methods: [], preferences: {} }
              },
              width: 'full'
            }
          ]
        }
      ]
    },
    {
      id: 'preferenze',
      title: 'Preferenze',
      description: 'Le tue preferenze per i servizi Coronatek',
      icon: MapPinIcon,
      collapsible: true,
      collapsed: false,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'user', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        {
          id: 'row-sede',
          columns: [
            {
              id: 'sede_preferita',
              field: {
                type: 'select',
                label: 'Sede Preferita',
                placeholder: 'Seleziona la sede preferita',
                required: false,
                icon: MapPinIcon,
                options: [
                  { value: '', label: 'Nessuna preferenza' },
                  { value: 'misterbianco', label: '🔵 Misterbianco (CT)' },
                  { value: 'palermo', label: '🟠 Palermo' }
                ],
                helpText: 'Indica la sede Coronatek che preferisci per i tuoi interventi'
              },
              width: '1/2'
            }
          ]
        }
      ]
    },
    {
      id: 'privacy-policy',
      title: 'Privacy Policy e Dichiarazioni',
      description: 'Consenso privacy e dichiarazioni obbligatorie',
      collapsible: false,
      collapsed: false,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'user', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        {
          id: 'row-1',
          columns: [
            {
              id: 'privacyAccepted',
              field: {
                type: 'checkbox',
                label: 'Ho letto e accetto la Privacy Policy',
                required: false
              },
              width: 'full'
            }
          ]
        },
        {
          id: 'row-2',
          columns: [
            {
              id: 'privacyLinks',
              field: {
                type: 'custom',
                customComponent: 'PrivacyLinks',
                label: '',
                required: false
              },
              width: 'full'
            }
          ]
        },
        {
          id: 'row-3',
          columns: [
            {
              id: 'dichiarazioneVeridicita',
              field: {
                type: 'checkbox',
                label: 'Dichiaro che i dati inseriti sono veritieri e completi',
                required: false
              },
              width: 'full'
            }
          ]
        }
      ]
    }
  ]
};

export const privatoConfig: FormConfig = {
  id: 'customer-registration-privato',
  title: 'Registrazione Cliente - Privato',
  description: 'Compila i tuoi dati personali per completare la registrazione',
  groups: [
    {
      id: 'dati-personali',
      title: 'Dati Personali',
      description: 'Informazioni personali del cliente',
      icon: UserIcon,
      collapsible: true,
      collapsed: false,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'user', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        // Prima riga: Titolo, Nome, Cognome
        {
          id: 'row-1',
          columns: [
            {
              id: 'titolo',
              field: {
                type: 'select',
                label: 'Titolo',
                placeholder: 'Seleziona titolo',
                required: false,
                defaultValue: 'sig',
                icon: UserCircleIcon,
                options: [
                  { value: 'sig', label: 'Sig' },
                  { value: 'sig.ra', label: 'Sig.ra' },
                  { value: 'dott', label: 'Dott' },
                  { value: 'dott.ssa', label: 'Dott.ssa' },
                  { value: 'ing', label: 'Ing' },
                  { value: 'avv', label: 'Avv' },
                  { value: 'prof', label: 'Prof' },
                  { value: 'prof.ssa', label: 'Prof.ssa' }
                ]
              },
              width: '1/6'
            },
            {
              id: 'nome',
              field: {
                type: 'text',
                label: 'Nome',
                placeholder: 'Inserisci il nome',
                required: false,
                formatter: 'capitalizeWords',
                icon: UserCircleIcon
              },
              width: '1/3'
            },
            {
              id: 'cognome',
              field: {
                type: 'text',
                label: 'Cognome',
                placeholder: 'Inserisci il cognome',
                required: false,
                formatter: 'capitalizeWords',
                icon: UserCircleIcon
              },
              width: '1/3'
            }
          ]
        },
        // Seconda riga: Prefisso Nazione, Codice Fiscale
        {
          id: 'row-2',
          columns: [
            {
              id: 'prefissoNazione',
              field: {
                type: 'custom',
                customComponent: 'CountrySelectField',
                label: 'Prefisso Nazione',
                placeholder: 'Seleziona nazione',
                required: false,
                defaultValue: 'IT',
                icon: GlobeIcon,
                options: [
                  { value: 'IT', label: 'Italia', flag: 'it' },
                  { value: 'DE', label: 'Germania', flag: 'de' },
                  { value: 'FR', label: 'Francia', flag: 'fr' },
                  { value: 'ES', label: 'Spagna', flag: 'es' },
                  { value: 'GB', label: 'Regno Unito', flag: 'gb' },
                  { value: 'AT', label: 'Austria', flag: 'at' },
                  { value: 'BE', label: 'Belgio', flag: 'be' },
                  { value: 'CH', label: 'Svizzera', flag: 'ch' },
                  { value: 'NL', label: 'Paesi Bassi', flag: 'nl' },
                  { value: 'SE', label: 'Svezia', flag: 'se' },
                  { value: 'NO', label: 'Norvegia', flag: 'no' },
                  { value: 'DK', label: 'Danimarca', flag: 'dk' },
                  { value: 'FI', label: 'Finlandia', flag: 'fi' },
                  { value: 'PL', label: 'Polonia', flag: 'pl' },
                  { value: 'CZ', label: 'Repubblica Ceca', flag: 'cz' },
                  { value: 'HU', label: 'Ungheria', flag: 'hu' },
                  { value: 'RO', label: 'Romania', flag: 'ro' },
                  { value: 'BG', label: 'Bulgaria', flag: 'bg' },
                  { value: 'HR', label: 'Croazia', flag: 'hr' },
                  { value: 'SI', label: 'Slovenia', flag: 'si' },
                  { value: 'SK', label: 'Slovacchia', flag: 'sk' },
                  { value: 'LT', label: 'Lituania', flag: 'lt' },
                  { value: 'LV', label: 'Lettonia', flag: 'lv' },
                  { value: 'EE', label: 'Estonia', flag: 'ee' },
                  { value: 'IE', label: 'Irlanda', flag: 'ie' },
                  { value: 'PT', label: 'Portogallo', flag: 'pt' },
                  { value: 'GR', label: 'Grecia', flag: 'gr' },
                  { value: 'CY', label: 'Cipro', flag: 'cy' },
                  { value: 'MT', label: 'Malta', flag: 'mt' },
                  { value: 'LU', label: 'Lussemburgo', flag: 'lu' },
                  // Paesi extra-UE
                  { value: 'US', label: 'Stati Uniti', flag: 'us' },
                  { value: 'CA', label: 'Canada', flag: 'ca' },
                  { value: 'AU', label: 'Australia', flag: 'au' },
                  { value: 'JP', label: 'Giappone', flag: 'jp' },
                  { value: 'CN', label: 'Cina', flag: 'cn' },
                  { value: 'IN', label: 'India', flag: 'in' },
                  { value: 'BR', label: 'Brasile', flag: 'br' },
                  { value: 'MX', label: 'Messico', flag: 'mx' }
                ]
              },
              width: '1/4'
            },
            {
              id: 'codiceFiscale',
              field: {
                type: 'text',
                label: 'Codice Fiscale',
                placeholder: 'Inserisci il Codice Fiscale (16 caratteri)',
                required: false,
                icon: DocumentIcon
              },
              width: '3/4'
            }
          ]
        },
        // Terza riga: PEC, Email
        {
          id: 'row-3',
          columns: [
            {
              id: 'pec',
              field: {
                type: 'email',
                label: 'PEC',
                placeholder: 'Inserisci la PEC (opzionale)',
                required: false,
                icon: EmailIcon
              },
              width: '1/2'
            },
            {
              id: 'email',
              field: {
                type: 'email',
                label: 'Email',
                placeholder: 'Inserisci l\'email',
                required: false,
                icon: EmailIcon
              },
              width: '1/2'
            }
          ]
        },
        // Quarta riga: Telefono con prefisso
        {
          id: 'row-4',
          columns: [
            {
              id: 'prefissoTelefono',
              field: {
                type: 'custom',
                customComponent: 'PhonePrefixSelectField',
                label: 'Prefisso Tel.',
                placeholder: 'Seleziona prefisso',
                required: false,
                // defaultValue: '+39', // Rimosso per permettere il caricamento dei dati dal DB
                icon: PhoneIcon,
                options: [
                  // Europa
                  { value: '+39', label: 'Italia', flag: 'it', group: 'Europa' },
                  { value: '+33', label: 'Francia', flag: 'fr', group: 'Europa' },
                  { value: '+49', label: 'Germania', flag: 'de', group: 'Europa' },
                  { value: '+44', label: 'Regno Unito', flag: 'gb', group: 'Europa' },
                  { value: '+34', label: 'Spagna', flag: 'es', group: 'Europa' },
                  { value: '+43', label: 'Austria', flag: 'at', group: 'Europa' },
                  { value: '+32', label: 'Belgio', flag: 'be', group: 'Europa' },
                  { value: '+41', label: 'Svizzera', flag: 'ch', group: 'Europa' },
                  { value: '+31', label: 'Paesi Bassi', flag: 'nl', group: 'Europa' },
                  { value: '+46', label: 'Svezia', flag: 'se', group: 'Europa' },
                  { value: '+47', label: 'Norvegia', flag: 'no', group: 'Europa' },
                  { value: '+45', label: 'Danimarca', flag: 'dk', group: 'Europa' },
                  { value: '+358', label: 'Finlandia', flag: 'fi', group: 'Europa' },
                  { value: '+48', label: 'Polonia', flag: 'pl', group: 'Europa' },
                  { value: '+420', label: 'Repubblica Ceca', flag: 'cz', group: 'Europa' },
                  { value: '+36', label: 'Ungheria', flag: 'hu', group: 'Europa' },
                  { value: '+40', label: 'Romania', flag: 'ro', group: 'Europa' },
                  { value: '+359', label: 'Bulgaria', flag: 'bg', group: 'Europa' },
                  { value: '+385', label: 'Croazia', flag: 'hr', group: 'Europa' },
                  { value: '+386', label: 'Slovenia', flag: 'si', group: 'Europa' },
                  { value: '+421', label: 'Slovacchia', flag: 'sk', group: 'Europa' },
                  { value: '+370', label: 'Lituania', flag: 'lt', group: 'Europa' },
                  { value: '+371', label: 'Lettonia', flag: 'lv', group: 'Europa' },
                  { value: '+372', label: 'Estonia', flag: 'ee', group: 'Europa' },
                  { value: '+353', label: 'Irlanda', flag: 'ie', group: 'Europa' },
                  { value: '+351', label: 'Portogallo', flag: 'pt', group: 'Europa' },
                  { value: '+30', label: 'Grecia', flag: 'gr', group: 'Europa' },
                  { value: '+357', label: 'Cipro', flag: 'cy', group: 'Europa' },
                  { value: '+356', label: 'Malta', flag: 'mt', group: 'Europa' },
                  { value: '+352', label: 'Lussemburgo', flag: 'lu', group: 'Europa' },
                  
                  // Americhe
                  { value: '+1', label: 'Stati Uniti', flag: 'us', group: 'Americhe' },
                  { value: '+1', label: 'Canada', flag: 'ca', group: 'Americhe' },
                  { value: '+52', label: 'Messico', flag: 'mx', group: 'Americhe' },
                  { value: '+55', label: 'Brasile', flag: 'br', group: 'Americhe' },
                  { value: '+54', label: 'Argentina', flag: 'ar', group: 'Americhe' },
                  { value: '+56', label: 'Cile', flag: 'cl', group: 'Americhe' },
                  { value: '+57', label: 'Colombia', flag: 'co', group: 'Americhe' },
                  { value: '+51', label: 'Perù', flag: 'pe', group: 'Americhe' },
                  { value: '+58', label: 'Venezuela', flag: 've', group: 'Americhe' },
                  { value: '+598', label: 'Uruguay', flag: 'uy', group: 'Americhe' },
                  { value: '+595', label: 'Paraguay', flag: 'py', group: 'Americhe' },
                  { value: '+593', label: 'Ecuador', flag: 'ec', group: 'Americhe' },
                  { value: '+591', label: 'Bolivia', flag: 'bo', group: 'Americhe' },
                  
                  // Asia
                  { value: '+86', label: 'Cina', flag: 'cn', group: 'Asia' },
                  { value: '+81', label: 'Giappone', flag: 'jp', group: 'Asia' },
                  { value: '+82', label: 'Corea del Sud', flag: 'kr', group: 'Asia' },
                  { value: '+91', label: 'India', flag: 'in', group: 'Asia' },
                  { value: '+65', label: 'Singapore', flag: 'sg', group: 'Asia' },
                  { value: '+60', label: 'Malesia', flag: 'my', group: 'Asia' },
                  { value: '+66', label: 'Thailandia', flag: 'th', group: 'Asia' },
                  { value: '+84', label: 'Vietnam', flag: 'vn', group: 'Asia' },
                  { value: '+63', label: 'Filippine', flag: 'ph', group: 'Asia' },
                  { value: '+62', label: 'Indonesia', flag: 'id', group: 'Asia' },
                  { value: '+886', label: 'Taiwan', flag: 'tw', group: 'Asia' },
                  { value: '+852', label: 'Hong Kong', flag: 'hk', group: 'Asia' },
                  { value: '+971', label: 'Emirati Arabi Uniti', flag: 'ae', group: 'Asia' },
                  { value: '+966', label: 'Arabia Saudita', flag: 'sa', group: 'Asia' },
                  { value: '+972', label: 'Israele', flag: 'il', group: 'Asia' },
                  { value: '+90', label: 'Turchia', flag: 'tr', group: 'Asia' },
                  
                  // Africa
                  { value: '+27', label: 'Sudafrica', flag: 'za', group: 'Africa' },
                  { value: '+20', label: 'Egitto', flag: 'eg', group: 'Africa' },
                  { value: '+234', label: 'Nigeria', flag: 'ng', group: 'Africa' },
                  { value: '+254', label: 'Kenya', flag: 'ke', group: 'Africa' },
                  { value: '+212', label: 'Marocco', flag: 'ma', group: 'Africa' },
                  { value: '+216', label: 'Tunisia', flag: 'tn', group: 'Africa' },
                  { value: '+213', label: 'Algeria', flag: 'dz', group: 'Africa' },
                  { value: '+233', label: 'Ghana', flag: 'gh', group: 'Africa' },
                  { value: '+251', label: 'Etiopia', flag: 'et', group: 'Africa' },
                  
                  // Oceania
                  { value: '+61', label: 'Australia', flag: 'au', group: 'Oceania' },
                  { value: '+64', label: 'Nuova Zelanda', flag: 'nz', group: 'Oceania' },
                  { value: '+679', label: 'Fiji', flag: 'fj', group: 'Oceania' },
                  { value: '+675', label: 'Papua Nuova Guinea', flag: 'pg', group: 'Oceania' }
                ]
              },
              width: '1/3'
            },
            {
              id: 'telefono',
              field: {
                type: 'text',
                label: 'Telefono',
                placeholder: 'Inserisci il numero di telefono',
                required: true,
                validation: {
                  pattern: '^[0-9.\-]+$',
                  message: 'Sono ammessi solo numeri, punti e trattini (-)'
                }
              },
              width: '1/3'
            }
          ]
        },
        // Quinta riga: Social checkbox
        {
          id: 'row-5',
          columns: [
            {
              id: 'social',
              field: {
                type: 'custom',
                customComponent: 'SocialCheckboxRow',
                label: '',
                required: false,
                defaultValue: { whatsapp: false, telegram: false }
              },
              width: 'full'
            }
          ]
        }
      ]
    },
    {
      id: 'indirizzo',
      title: 'Indirizzo',
      description: 'Residenza',
      icon: MapPinIcon,
      collapsible: true,
      collapsed: false,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        {
          id: 'row-1',
          columns: [
            {
              id: 'indirizzo',
              field: {
                type: 'custom',
                customComponent: 'AddressSection',
                label: 'Indirizzo Completo',
                placeholder: 'Cerca e seleziona l\'indirizzo...',
                required: false,
                helpText: 'Usa l\'autocomplete per cercare l\'indirizzo o compila manualmente i campi'
              },
              width: 'full'
            }
          ]
        },
        {
          id: 'row-2',
          columns: [
            {
              id: 'noteIndirizzo',
              field: {
                type: 'text',
                label: 'Note Indirizzo',
                placeholder: 'Note aggiuntive per l\'indirizzo (max 500 caratteri)',
                required: false,
                maxLength: 500
              },
              width: 'full'
            }
          ]
        }
      ]
    },
    {
      id: 'meccanico-fiducia',
      title: 'Meccanico di Fiducia',
      description: 'Dati del meccanico di fiducia (opzionale)',
      icon: UserIcon,
      collapsible: true,
      collapsed: true,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'user', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        // Prima riga: Ragione Sociale, Nome, Cognome
        {
          id: 'meccanico-row-1',
          columns: [
            {
              id: 'meccanicoRagioneSociale',
              field: {
                type: 'text',
                label: 'Ragione Sociale',
                placeholder: 'Inserisci la ragione sociale',
                required: false,
                formatter: 'capitalizeWords',
                icon: BuildingIcon
              },
              width: '1/2'
            },
            {
              id: 'meccanicoNome',
              field: {
                type: 'text',
                label: 'Nome',
                placeholder: 'Inserisci il nome',
                required: false,
                formatter: 'capitalizeWords',
                icon: UserCircleIcon
              },
              width: '1/4'
            },
            {
              id: 'meccanicoCognome',
              field: {
                type: 'text',
                label: 'Cognome',
                placeholder: 'Inserisci il cognome',
                required: false,
                formatter: 'capitalizeWords',
                icon: UserCircleIcon
              },
              width: '1/4'
            }
          ]
        },
        // Seconda riga: Email, Website, Prefisso Telefono, Telefono
        {
          id: 'meccanico-row-2',
          columns: [
            {
              id: 'meccanicoEmail',
              field: {
                type: 'email',
                label: 'Email',
                placeholder: 'Inserisci l\'email',
                required: false,
                icon: EmailIcon,
                validation: {
                  email: true
                }
              },
              width: '1/3'
            },
            {
              id: 'meccanicoWebsite',
              field: {
                type: 'url',
                label: 'Website',
                placeholder: 'Inserisci il sito web',
                required: false,
                icon: GlobeIcon,
                validation: {
                  url: true
                }
              },
              width: '1/3'
            },
            {
              id: 'meccanicoPrefissoTelefono',
              field: {
                type: 'custom',
                customComponent: 'PhonePrefixSelectField',
                label: 'Prefisso Tel.',
                placeholder: 'Seleziona prefisso',
                required: false,
                // defaultValue: '+39', // Rimosso per permettere il caricamento dei dati dal DB
                icon: PhoneIcon,
                options: [
                  // Europa
                  { value: '+39', label: 'Italia', flag: 'it', group: 'Europa' },
                  { value: '+33', label: 'Francia', flag: 'fr', group: 'Europa' },
                  { value: '+49', label: 'Germania', flag: 'de', group: 'Europa' },
                  { value: '+44', label: 'Regno Unito', flag: 'gb', group: 'Europa' },
                  { value: '+34', label: 'Spagna', flag: 'es', group: 'Europa' },
                  { value: '+43', label: 'Austria', flag: 'at', group: 'Europa' },
                  { value: '+32', label: 'Belgio', flag: 'be', group: 'Europa' },
                  { value: '+41', label: 'Svizzera', flag: 'ch', group: 'Europa' },
                  { value: '+31', label: 'Paesi Bassi', flag: 'nl', group: 'Europa' },
                  { value: '+46', label: 'Svezia', flag: 'se', group: 'Europa' },
                  { value: '+47', label: 'Norvegia', flag: 'no', group: 'Europa' },
                  { value: '+45', label: 'Danimarca', flag: 'dk', group: 'Europa' },
                  { value: '+358', label: 'Finlandia', flag: 'fi', group: 'Europa' },
                  { value: '+48', label: 'Polonia', flag: 'pl', group: 'Europa' },
                  { value: '+420', label: 'Repubblica Ceca', flag: 'cz', group: 'Europa' },
                  { value: '+36', label: 'Ungheria', flag: 'hu', group: 'Europa' },
                  { value: '+40', label: 'Romania', flag: 'ro', group: 'Europa' },
                  { value: '+359', label: 'Bulgaria', flag: 'bg', group: 'Europa' },
                  { value: '+385', label: 'Croazia', flag: 'hr', group: 'Europa' },
                  { value: '+386', label: 'Slovenia', flag: 'si', group: 'Europa' },
                  { value: '+421', label: 'Slovacchia', flag: 'sk', group: 'Europa' },
                  { value: '+370', label: 'Lituania', flag: 'lt', group: 'Europa' },
                  { value: '+371', label: 'Lettonia', flag: 'lv', group: 'Europa' },
                  { value: '+372', label: 'Estonia', flag: 'ee', group: 'Europa' },
                  { value: '+353', label: 'Irlanda', flag: 'ie', group: 'Europa' },
                  { value: '+351', label: 'Portogallo', flag: 'pt', group: 'Europa' },
                  { value: '+30', label: 'Grecia', flag: 'gr', group: 'Europa' },
                  { value: '+357', label: 'Cipro', flag: 'cy', group: 'Europa' },
                  { value: '+356', label: 'Malta', flag: 'mt', group: 'Europa' },
                  { value: '+352', label: 'Lussemburgo', flag: 'lu', group: 'Europa' }
                ]
              },
              width: '1/6'
            },
            {
              id: 'meccanicoTelefono',
              field: {
                type: 'phone',
                label: 'Telefono',
                placeholder: 'Inserisci numero di telefono',
                required: false,
                icon: PhoneIcon,
                validation: {
                  pattern: '^[0-9.\-]+$',
                  message: 'Sono ammessi solo numeri, punti e trattini (-)'
                }
              },
              width: '1/6'
            }
          ]
        },
        // Terza riga: Social checkbox
        {
          id: 'meccanico-row-3',
          columns: [
            {
              id: 'meccanicoSocial',
              field: {
                type: 'custom',
                customComponent: 'SocialCheckboxRow',
                label: '',
                required: false,
                defaultValue: { whatsapp: false, telegram: false }
              },
              width: 'full'
            }
          ]
        },
        // Quarta riga: Indirizzo completo
        {
          id: 'meccanico-row-4',
          columns: [
            {
              id: 'meccanicoIndirizzo',
              field: {
                type: 'custom',
                customComponent: 'AddressSection',
                label: 'Indirizzo Completo',
                placeholder: 'Cerca e seleziona l\'indirizzo...',
                required: false,
                helpText: 'Usa l\'autocomplete per cercare l\'indirizzo o compila manualmente i campi'
              },
              width: 'full'
            }
          ]
        }
      ]
    },
    {
      id: 'contatti-referente-privato',
      title: 'Contatti Referente',
      description: 'Informazioni del referente (opzionale)',
      icon: UserGroupIcon,
      collapsible: true,
      collapsed: true,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'user', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        // Prima riga: Titolo, Nome, Cognome, Email
        {
          id: 'referente-privato-row-1',
          columns: [
            {
              id: 'referentePrivatoTitolo',
              field: {
                type: 'select',
                label: 'Titolo',
                placeholder: 'Seleziona titolo',
                required: false,
                defaultValue: 'sig',
                icon: UserCircleIcon,
                options: [
                  { value: 'sig', label: 'Sig' },
                  { value: 'sig.ra', label: 'Sig.ra' },
                  { value: 'dott', label: 'Dott' },
                  { value: 'dott.ssa', label: 'Dott.ssa' },
                  { value: 'ing', label: 'Ing' },
                  { value: 'avv', label: 'Avv' },
                  { value: 'prof', label: 'Prof' },
                  { value: 'prof.ssa', label: 'Prof.ssa' }
                ]
              },
              width: '2/12'
            },
            {
              id: 'referentePrivatoNome',
              field: {
                type: 'text',
                label: 'Nome',
                placeholder: 'Inserisci il nome',
                required: false,
                formatter: 'capitalizeWords',
                icon: UserCircleIcon
              },
              width: '3/12'
            },
            {
              id: 'referentePrivatoCognome',
              field: {
                type: 'text',
                label: 'Cognome',
                placeholder: 'Inserisci il cognome',
                required: false,
                formatter: 'capitalizeWords',
                icon: UserCircleIcon
              },
              width: '3/12'
            },
            {
              id: 'referentePrivatoEmail',
              field: {
                type: 'email',
                label: 'E-Mail',
                placeholder: 'Inserisci l\'email',
                required: false,
                icon: EmailIcon,
                validation: {
                  email: true
                }
              },
              width: '4/12'
            }
          ]
        },
        // Seconda riga: Prefisso Fisso, Telefono Fisso, Prefisso Cell, Cellulare
        {
          id: 'referente-privato-row-2',
          columns: [
            {
              id: 'referentePrivatoPrefissoFisso',
              field: {
                type: 'custom',
                customComponent: 'PhonePrefixSelectField',
                label: 'Prefisso Int.',
                placeholder: 'Seleziona prefisso',
                required: false,
                defaultValue: '+39', // Default per Italia, si adatta ai dati dal DB quando presenti
                icon: PhoneIcon,
                options: [
                  // Europa
                  { value: '+39', label: 'Italia', flag: 'it', group: 'Europa' },
                  { value: '+33', label: 'Francia', flag: 'fr', group: 'Europa' },
                  { value: '+49', label: 'Germania', flag: 'de', group: 'Europa' },
                  { value: '+44', label: 'Regno Unito', flag: 'gb', group: 'Europa' },
                  { value: '+34', label: 'Spagna', flag: 'es', group: 'Europa' },
                  { value: '+43', label: 'Austria', flag: 'at', group: 'Europa' },
                  { value: '+32', label: 'Belgio', flag: 'be', group: 'Europa' },
                  { value: '+41', label: 'Svizzera', flag: 'ch', group: 'Europa' },
                  { value: '+31', label: 'Paesi Bassi', flag: 'nl', group: 'Europa' },
                  { value: '+46', label: 'Svezia', flag: 'se', group: 'Europa' },
                  { value: '+47', label: 'Norvegia', flag: 'no', group: 'Europa' },
                  { value: '+45', label: 'Danimarca', flag: 'dk', group: 'Europa' },
                  { value: '+358', label: 'Finlandia', flag: 'fi', group: 'Europa' },
                  { value: '+48', label: 'Polonia', flag: 'pl', group: 'Europa' },
                  { value: '+420', label: 'Repubblica Ceca', flag: 'cz', group: 'Europa' },
                  { value: '+36', label: 'Ungheria', flag: 'hu', group: 'Europa' },
                  { value: '+40', label: 'Romania', flag: 'ro', group: 'Europa' },
                  { value: '+359', label: 'Bulgaria', flag: 'bg', group: 'Europa' },
                  { value: '+385', label: 'Croazia', flag: 'hr', group: 'Europa' },
                  { value: '+386', label: 'Slovenia', flag: 'si', group: 'Europa' },
                  { value: '+421', label: 'Slovacchia', flag: 'sk', group: 'Europa' },
                  { value: '+370', label: 'Lituania', flag: 'lt', group: 'Europa' },
                  { value: '+371', label: 'Lettonia', flag: 'lv', group: 'Europa' },
                  { value: '+372', label: 'Estonia', flag: 'ee', group: 'Europa' },
                  { value: '+353', label: 'Irlanda', flag: 'ie', group: 'Europa' },
                  { value: '+351', label: 'Portogallo', flag: 'pt', group: 'Europa' },
                  { value: '+30', label: 'Grecia', flag: 'gr', group: 'Europa' },
                  { value: '+357', label: 'Cipro', flag: 'cy', group: 'Europa' },
                  { value: '+356', label: 'Malta', flag: 'mt', group: 'Europa' },
                  { value: '+352', label: 'Lussemburgo', flag: 'lu', group: 'Europa' }
                ]
              },
              width: '3/12'
            },
            {
              id: 'referentePrivatoTelefonoFisso',
              field: {
                type: 'phone',
                label: 'Telefono Fisso',
                placeholder: 'Inserisci numero di telefono fisso',
                required: false,
                icon: PhoneIcon,
                validation: {
                  pattern: '^[0-9.\-]+$',
                  message: 'Sono ammessi solo numeri, punti e trattini (-)'
                }
              },
              width: '3/12'
            },
            {
              id: 'referentePrivatoPrefissoCellulare',
              field: {
                type: 'custom',
                customComponent: 'PhonePrefixSelectField',
                label: 'Prefisso Int.',
                placeholder: 'Seleziona prefisso',
                required: false,
                defaultValue: '+39', // Default per Italia, si adatta ai dati dal DB quando presenti
                icon: PhoneIcon,
                options: [
                  // Europa
                  { value: '+39', label: 'Italia', flag: 'it', group: 'Europa' },
                  { value: '+33', label: 'Francia', flag: 'fr', group: 'Europa' },
                  { value: '+49', label: 'Germania', flag: 'de', group: 'Europa' },
                  { value: '+44', label: 'Regno Unito', flag: 'gb', group: 'Europa' },
                  { value: '+34', label: 'Spagna', flag: 'es', group: 'Europa' },
                  { value: '+43', label: 'Austria', flag: 'at', group: 'Europa' },
                  { value: '+32', label: 'Belgio', flag: 'be', group: 'Europa' },
                  { value: '+41', label: 'Svizzera', flag: 'ch', group: 'Europa' },
                  { value: '+31', label: 'Paesi Bassi', flag: 'nl', group: 'Europa' },
                  { value: '+46', label: 'Svezia', flag: 'se', group: 'Europa' },
                  { value: '+47', label: 'Norvegia', flag: 'no', group: 'Europa' },
                  { value: '+45', label: 'Danimarca', flag: 'dk', group: 'Europa' },
                  { value: '+358', label: 'Finlandia', flag: 'fi', group: 'Europa' },
                  { value: '+48', label: 'Polonia', flag: 'pl', group: 'Europa' },
                  { value: '+420', label: 'Repubblica Ceca', flag: 'cz', group: 'Europa' },
                  { value: '+36', label: 'Ungheria', flag: 'hu', group: 'Europa' },
                  { value: '+40', label: 'Romania', flag: 'ro', group: 'Europa' },
                  { value: '+359', label: 'Bulgaria', flag: 'bg', group: 'Europa' },
                  { value: '+385', label: 'Croazia', flag: 'hr', group: 'Europa' },
                  { value: '+386', label: 'Slovenia', flag: 'si', group: 'Europa' },
                  { value: '+421', label: 'Slovacchia', flag: 'sk', group: 'Europa' },
                  { value: '+370', label: 'Lituania', flag: 'lt', group: 'Europa' },
                  { value: '+371', label: 'Lettonia', flag: 'lv', group: 'Europa' },
                  { value: '+372', label: 'Estonia', flag: 'ee', group: 'Europa' },
                  { value: '+353', label: 'Irlanda', flag: 'ie', group: 'Europa' },
                  { value: '+351', label: 'Portogallo', flag: 'pt', group: 'Europa' },
                  { value: '+30', label: 'Grecia', flag: 'gr', group: 'Europa' },
                  { value: '+357', label: 'Cipro', flag: 'cy', group: 'Europa' },
                  { value: '+356', label: 'Malta', flag: 'mt', group: 'Europa' },
                  { value: '+352', label: 'Lussemburgo', flag: 'lu', group: 'Europa' }
                ]
              },
              width: '3/12'
            },
            {
              id: 'referentePrivatoCellulare',
              field: {
                type: 'phone',
                label: 'Cellulare',
                placeholder: 'Inserisci numero di cellulare',
                required: false,
                icon: PhoneIcon,
                validation: {
                  pattern: '^[0-9.\-]+$',
                  message: 'Sono ammessi solo numeri, punti e trattini (-)'
                }
              },
              width: '3/12'
            }
          ]
        },
        // Terza riga: Social checkbox
        {
          id: 'referente-privato-row-3',
          columns: [
            {
              id: 'referentePrivatoSocial',
              field: {
                type: 'custom',
                customComponent: 'SocialCheckboxRow',
                label: '',
                required: false,
                defaultValue: { whatsapp: false, telegram: false }
              },
              width: 'full'
            }
          ]
        }
      ]
    },
    {
      id: 'modalita-pagamento',
      title: 'Modalità di Pagamento',
      description: 'Scegli le tue modalità di pagamento preferite',
      icon: CreditCardIcon,
      collapsible: true,
      collapsed: true,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'user', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        {
          id: 'payment-methods-row',
          columns: [
            {
              id: 'paymentMethods',
              field: {
                type: 'custom',
                customComponent: 'PaymentMethodsCustom',
                label: '',
                required: false,
                defaultValue: { methods: [], preferences: {} }
              },
              width: 'full'
            }
          ]
        }
      ]
    },
    {
      id: 'modalita-consegna',
      title: 'Modalità di Consegna',
      description: 'Scegli le tue modalità di consegna preferite',
      icon: TruckIcon,
      collapsible: true,
      collapsed: true,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'user', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        {
          id: 'delivery-methods-row',
          columns: [
            {
              id: 'deliveryMethods',
              field: {
                type: 'custom',
                customComponent: 'DeliveryMethodsCustom',
                label: '',
                required: false,
                defaultValue: { methods: [], preferences: {} }
              },
              width: 'full'
            }
          ]
        }
      ]
    },
    {
      id: 'preferenze',
      title: 'Preferenze',
      description: 'Le tue preferenze per i servizi Coronatek',
      icon: MapPinIcon,
      collapsible: true,
      collapsed: false,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'user', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        {
          id: 'row-sede',
          columns: [
            {
              id: 'sede_preferita',
              field: {
                type: 'select',
                label: 'Sede Preferita',
                placeholder: 'Seleziona la sede preferita',
                required: false,
                icon: MapPinIcon,
                options: [
                  { value: '', label: 'Nessuna preferenza' },
                  { value: 'misterbianco', label: '🔵 Misterbianco (CT)' },
                  { value: 'palermo', label: '🟠 Palermo' }
                ],
                helpText: 'Indica la sede Coronatek che preferisci per i tuoi interventi'
              },
              width: '1/2'
            }
          ]
        }
      ]
    },
    {
      id: 'privacy-policy',
      title: 'Privacy Policy e Dichiarazioni',
      description: 'Consenso privacy e dichiarazioni obbligatorie',
      collapsible: false,
      collapsed: false,
      permissions: {
        view: ['admin', 'manager', 'user', 'superadmin'],
        edit: ['admin', 'manager', 'user', 'superadmin'],
        delete: ['admin', 'superadmin']
      },
      rows: [
        {
          id: 'row-1',
          columns: [
            {
              id: 'privacyAccepted',
              field: {
                type: 'checkbox',
                label: 'Ho letto e accetto la Privacy Policy',
                required: false
              },
              width: 'full'
            }
          ]
        },
        {
          id: 'row-2',
          columns: [
            {
              id: 'privacyLinks',
              field: {
                type: 'custom',
                customComponent: 'PrivacyLinks',
                label: '',
                required: false
              },
              width: 'full'
            }
          ]
        },
        {
          id: 'row-3',
          columns: [
            {
              id: 'dichiarazioneVeridicita',
              field: {
                type: 'checkbox',
                label: 'Dichiaro che i dati inseriti sono veritieri e completi',
                required: false
              },
              width: 'full'
            }
          ]
        }
      ]
    }
  ]
};

// Configurazione combinata con switch
export const customerRegistrationConfig = {
  azienda: aziendaConfig,
  privato: privatoConfig
}; 